import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChatCircleDots, MagnifyingGlass, PaperPlaneRight, Paperclip, DotsThreeVertical,
  PencilSimple, Trash, Plus, Users, X as XIcon, Check, ArrowUDownLeft, Megaphone,
  UsersThree, User as UserIcon, FileText, Image as ImageIcon, DownloadSimple,
  BellSlash, Bell, FolderOpen,
} from "@phosphor-icons/react";
import { ROLE_LABEL } from "@/lib/roles";
import { toast } from "sonner";
import { getToken } from "@/lib/authToken";
/* ============================== Utils ============================== */

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    const diffDays = (now - d) / (1000 * 60 * 60 * 24);
    if (diffDays < 7) return d.toLocaleDateString("es-ES", { weekday: "short" });
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
};

const fmtFullTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const initialsOf = (name) => {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

export const conversationTitle = (conv, currentUserId) => {
  if (conv.type === "general") return conv.name || "General";
  if (conv.type === "group") return conv.name || "Grupo sin nombre";
  if (conv.type === "expediente") return conv.name || "Expediente";
  if (conv.type === "direct") {
    const other = (conv.member_details || []).find((m) => m.id !== currentUserId);
    return other?.name || "Conversación";
  }
  return conv.name || "Conversación";
};

const conversationAvatar = (conv, currentUserId) => {
  if (conv.type === "direct") {
    const other = (conv.member_details || []).find((m) => m.id !== currentUserId);
    return { initials: initialsOf(other?.name || "?"), kind: "user" };
  }
  if (conv.type === "general") return { initials: "GE", kind: "general" };
  if (conv.type === "expediente") return { initials: "EX", kind: "expediente" };
  return { initials: initialsOf(conv.name || "GR"), kind: "group" };
};

/* ============================== Conversation Row ============================== */

function ConversationRow({ conv, active, onClick, currentUserId }) {
  const title = conversationTitle(conv, currentUserId);
  const av = conversationAvatar(conv, currentUserId);
  const lastSender = (conv.member_details || []).find((m) => m.id === conv.last_message_sender_id);
  const youSent = conv.last_message_sender_id === currentUserId;
  const preview = conv.last_message_preview
    ? `${youSent ? "Tú: " : (lastSender?.name ? `${lastSender.name.split(" ")[0]}: ` : "")}${conv.last_message_preview}`
    : "Sin mensajes";

  const avatarColor = {
    general: "bg-amber-500/15 text-amber-700 ring-amber-500/30",
    group: "bg-indigo-500/15 text-indigo-700 ring-indigo-500/30",
    expediente: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30",
    user: "bg-brand/10 text-brand ring-brand/30",
  }[av.kind];

  return (
    <button
      onClick={onClick}
      data-testid={`chat-conv-${conv.id}`}
      className={`w-full text-left px-3 py-2.5 flex gap-3 items-center transition-colors border-l-2 ${
        active ? "bg-brand/5 border-l-brand" : "border-l-transparent hover:bg-muted/40"
      }`}
    >
      <Avatar className={`h-10 w-10 ring-2 shrink-0 ${avatarColor}`}>
        <AvatarFallback className="bg-transparent font-semibold text-xs">
          {av.kind === "general" ? <Megaphone className="w-5 h-5" weight="fill" /> :
           av.kind === "group" ? <UsersThree className="w-5 h-5" weight="fill" /> :
           av.kind === "expediente" ? <FileText className="w-5 h-5" weight="fill" /> :
           av.initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm truncate flex items-center gap-1.5">
            {title}
            {conv.muted_by?.includes(currentUserId) && (
              <BellSlash className="w-3 h-3 text-muted-foreground shrink-0" weight="fill" />
            )}
          </div>
          <div className="text-[10px] text-muted-foreground shrink-0">
            {fmtTime(conv.last_message_at)}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-xs text-muted-foreground truncate">{preview}</div>
          {conv.unread_count > 0 && (
            <Badge data-testid={`chat-unread-${conv.id}`} className="bg-brand text-white text-[10px] h-5 min-w-[20px] px-1.5 shrink-0">
              {conv.unread_count}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

/* ============================== Conversations List ============================== */

export function ConversationsList({ conversations, activeId, onSelect, onNew, currentUserId }) {
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const title = conversationTitle(c, currentUserId).toLowerCase();
      return title.includes(q);
    });
  }, [filter, conversations, currentUserId]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-3 border-b border-border space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <ChatCircleDots className="w-4 h-4 text-brand" weight="fill" />
            Conversaciones
          </h2>
          <Button
            size="sm"
            variant="default"
            onClick={onNew}
            data-testid="chat-new-button"
            className="h-8 px-2"
          >
            <Plus className="w-4 h-4" weight="bold" />
          </Button>
        </div>
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="chat-search-conv-input"
            className="pl-8 h-9 text-sm"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        {filtered.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-8 px-4">
            {filter ? "Sin coincidencias" : "Aún no tienes conversaciones"}
          </div>
        ) : (
          filtered.map((c) => (
            <ConversationRow
              key={c.id}
              conv={c}
              active={c.id === activeId}
              onClick={() => onSelect(c)}
              currentUserId={currentUserId}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
}

/* ============================== Message Bubble ============================== */

function renderContentWithMentions(text, mine = false, onMentionClick = null) {
  if (!text) return null;
  const parts = text.split(/(@[\w\.\-]+)/g);
  const baseCls = mine
    ? "bg-white/25 text-white font-semibold px-1 rounded"
    : "bg-brand/10 text-brand font-semibold px-1 rounded";
  return parts.map((p, i) =>
    p.startsWith("@") ? (
      onMentionClick ? (
        <button
          key={`m-${i}-${p}`}
          type="button"
          onClick={(e) => { e.stopPropagation(); onMentionClick(p.slice(1)); }}
          className={`${baseCls} hover:underline cursor-pointer`}
          data-testid={`mention-${p.slice(1)}`}
          title="Ir al expediente"
        >{p}</button>
      ) : (
        <span key={`m-${i}-${p}`} className={baseCls}>{p}</span>
      )
    ) : (
      <span key={`t-${i}-${p}`}>{p}</span>
    )
  );
}

function AttachmentPreview({ att }) {
  const isImage = (att.mime_type || "").startsWith("image/");
  const url = `${API}/chat/attachments/${att.id}`;
  const token = getToken();
  const authedUrl = `${url}`;

  // Para imágenes usamos un fetch con auth para mostrarlas inline
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!isImage) return;
    let mounted = true;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => mounted && setBlobUrl(URL.createObjectURL(b)))
      .catch(() => {});
    return () => { mounted = false; };
  }, [url, isImage, token]);

  const download = async () => {
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const b = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = att.filename || "archivo";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("No se pudo descargar"); }
  };

  if (isImage && blobUrl) {
    return (
      <button onClick={download} className="block max-w-[280px] rounded-md overflow-hidden border border-border" data-testid={`chat-att-${att.id}`}>
        <img src={blobUrl} alt={att.filename} className="block max-h-[260px]" />
      </button>
    );
  }
  return (
    <button
      onClick={download}
      data-testid={`chat-att-${att.id}`}
      className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-md hover:bg-muted/70 transition-colors text-xs max-w-[280px]"
    >
      {isImage ? <ImageIcon className="w-4 h-4 shrink-0" weight="fill" /> : <FileText className="w-4 h-4 shrink-0" weight="fill" />}
      <span className="truncate flex-1 text-left">{att.filename}</span>
      <DownloadSimple className="w-4 h-4 shrink-0 text-muted-foreground" weight="bold" />
    </button>
  );
}

function MessageBubble({ msg, mine, sender, onEdit, onDelete, onReply, onMentionClick }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(msg.content);

  const submitEdit = async () => {
    const t = editText.trim();
    if (!t) return;
    await onEdit(msg.id, t);
    setEditing(false);
  };

  if (msg.deleted) {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`}>
        <div className="text-xs text-muted-foreground italic px-3 py-1.5 rounded-2xl bg-muted/30">
          Mensaje eliminado
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-2 group`} data-testid={`chat-msg-${msg.id}`}>
      {!mine && (
        <Avatar className="h-7 w-7 mr-2 ring-1 ring-brand/20 shrink-0 mt-0.5">
          <AvatarFallback className="bg-brand/10 text-brand text-[10px] font-semibold">
            {initialsOf(sender?.name)}
          </AvatarFallback>
        </Avatar>
      )}
      <div className={`max-w-[75%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
        {!mine && (
          <div className="text-[10px] text-muted-foreground font-semibold mb-0.5 px-1">
            {sender?.name || "Usuario"}
          </div>
        )}
        <div className={`relative px-3 py-2 rounded-2xl text-sm shadow-sm ${
          mine ? "bg-brand text-white rounded-br-sm" : "bg-muted/60 text-foreground rounded-bl-sm"
        }`}>
          {editing ? (
            <div className="flex flex-col gap-2 min-w-[220px]">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
                className="text-sm bg-white text-foreground"
                data-testid="chat-msg-edit-input"
              />
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="secondary" onClick={() => setEditing(false)} className="h-7 px-2"><XIcon className="w-3 h-3" /></Button>
                <Button size="sm" onClick={submitEdit} className="h-7 px-2" data-testid="chat-msg-edit-save"><Check className="w-3 h-3" /></Button>
              </div>
            </div>
          ) : (
            <>
              {msg.content && (
                <div className="whitespace-pre-wrap break-words">
                  {renderContentWithMentions(msg.content, mine, onMentionClick)}
                </div>
              )}
              {(msg.attachments || []).length > 0 && (
                <div className={`flex flex-col gap-1.5 ${msg.content ? "mt-2" : ""}`}>
                  {msg.attachments.map((a) => <AttachmentPreview key={a.id} att={a} />)}
                </div>
              )}
            </>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-0.5 px-1 text-[10px] text-muted-foreground ${mine ? "flex-row-reverse" : ""}`}>
          <span>{fmtFullTime(msg.created_at)}</span>
          {msg.edited && <span>· editado</span>}
          {!editing && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`chat-msg-menu-${msg.id}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-muted/60 rounded"
                >
                  <DotsThreeVertical className="w-3.5 h-3.5" weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={mine ? "end" : "start"} className="text-xs">
                <DropdownMenuItem onClick={() => onReply(msg)} data-testid={`chat-msg-reply-${msg.id}`}>
                  <ArrowUDownLeft className="w-3 h-3 mr-2" /> Responder
                </DropdownMenuItem>
                {mine && (
                  <>
                    <DropdownMenuItem onClick={() => { setEditText(msg.content); setEditing(true); }} data-testid={`chat-msg-edit-${msg.id}`}>
                      <PencilSimple className="w-3 h-3 mr-2" /> Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(msg.id)} className="text-red-600" data-testid={`chat-msg-delete-${msg.id}`}>
                      <Trash className="w-3 h-3 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================== Chat Window ============================== */

export function ChatWindow({ conversation, onConversationChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { markRead, refreshConversations, toggleMute, notifPermission, requestNotifications } = useChat();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const pollRef = useRef(null);

  const sendersById = useMemo(() => {
    const map = {};
    (conversation?.member_details || []).forEach((m) => { map[m.id] = m; });
    return map;
  }, [conversation]);

  const loadMessages = async () => {
    if (!conversation) return;
    try {
      const { data } = await api.get(`/chat/conversations/${conversation.id}/messages`, {
        params: { limit: 80 },
      });
      setMessages(data || []);
      if (data?.length) lastTsRef.current = data[data.length - 1].created_at;
      await markRead(conversation.id);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
      }, 50);
    } catch (_e) { /* silent */ }
  };

  const fetchIncremental = async () => {
    if (!conversation || !lastTsRef.current) return;
    try {
      const { data } = await api.get(`/chat/conversations/${conversation.id}/messages`, {
        params: { after: lastTsRef.current, limit: 50 },
      });
      if (data?.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = data.filter((m) => !seen.has(m.id));
          if (!fresh.length) return prev;
          lastTsRef.current = fresh[fresh.length - 1].created_at;
          return [...prev, ...fresh];
        });
        await markRead(conversation.id);
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        }, 50);
      }
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    if (!conversation) return;
    setReplyTo(null);
    setSearching(false);
    setSearchQ("");
    setEditTitle(false);
    loadMessages();
    pollRef.current = setInterval(fetchIncremental, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/conversations/${conversation.id}/messages`, {
        content: text,
        reply_to: replyTo?.id || null,
      });
      setMessages((prev) => [...prev, data]);
      lastTsRef.current = data.created_at;
      setText("");
      setReplyTo(null);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
      refreshConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally { setSending(false); }
  };

  const onPickFiles = async (filesList) => {
    if (!filesList?.length) return;
    const fd = new FormData();
    for (const f of filesList) fd.append("files", f);
    if (text.trim()) fd.append("content", text);
    try {
      const { data } = await api.post(
        `/chat/conversations/${conversation.id}/attachments`,
        fd,
      );
      setMessages((prev) => [...prev, data]);
      lastTsRef.current = data.created_at;
      setText("");
      refreshConversations();
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el archivo");
    }
  };

  const editMessage = async (id, content) => {
    try {
      const { data } = await api.patch(`/chat/messages/${id}`, { content });
      setMessages((prev) => prev.map((m) => m.id === id ? data : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo editar");
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/chat/messages/${id}`);
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, deleted: true, content: "", attachments: [] } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    }
  };

  const runSearch = async () => {
    if (searchQ.trim().length < 2) return;
    try {
      const { data } = await api.get("/chat/search", {
        params: { q: searchQ, conversation_id: conversation.id, limit: 30 },
      });
      setSearchResults(data || []);
    } catch (e) { toast.error("Error en búsqueda"); }
  };

  const saveTitle = async () => {
    try {
      const { data } = await api.patch(`/chat/conversations/${conversation.id}`, { name: titleDraft });
      setEditTitle(false);
      onConversationChanged?.(data);
      refreshConversations();
    } catch (e) { toast.error("No se pudo renombrar"); }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm bg-card">
        Selecciona una conversación
      </div>
    );
  }

  const title = conversationTitle(conversation, user.id);
  const av = conversationAvatar(conversation, user.id);
  const canEditMeta = conversation.type === "group";

  return (
    <div className="flex-1 flex flex-col bg-card min-w-0">
      {/* Header */}
      <div className="h-14 px-4 border-b border-border flex items-center gap-3 shrink-0">
        <Avatar className="h-9 w-9 ring-2 ring-brand/20 shrink-0 bg-brand/10 text-brand">
          <AvatarFallback className="bg-transparent font-semibold text-xs">
            {av.kind === "general" ? <Megaphone className="w-4 h-4" weight="fill" /> :
             av.kind === "group" ? <UsersThree className="w-4 h-4" weight="fill" /> :
             av.kind === "expediente" ? <FileText className="w-4 h-4" weight="fill" /> :
             av.initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          {editTitle ? (
            <div className="flex gap-2">
              <Input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} className="h-7 text-sm" />
              <Button size="sm" onClick={saveTitle} className="h-7 px-2"><Check className="w-3 h-3" /></Button>
              <Button size="sm" variant="secondary" onClick={() => setEditTitle(false)} className="h-7 px-2"><XIcon className="w-3 h-3" /></Button>
            </div>
          ) : (
            <button
              onClick={() => { if (canEditMeta) { setEditTitle(true); setTitleDraft(title); } }}
              className={`text-left ${canEditMeta ? "hover:underline cursor-pointer" : "cursor-default"}`}
              data-testid="chat-conv-title"
            >
              <div className="text-sm font-semibold truncate">{title}</div>
              <div className="text-[11px] text-muted-foreground">
                {(conversation.member_details || []).length} miembros
              </div>
            </button>
          )}
        </div>
        <button
          onClick={() => setShowMembers(true)}
          className="p-2 rounded hover:bg-muted/40 text-muted-foreground"
          data-testid="chat-show-members"
          title="Miembros"
        >
          <Users className="w-4 h-4" weight="bold" />
        </button>
        {/* Atajo: abrir el expediente asociado al chat (sólo conversaciones tipo expediente) */}
        {conversation.type === "expediente" && conversation.expediente_id && (
          <button
            onClick={() => {
              // Aviso al drawer padre para minimizarse y permitir seguir
              // viendo el chat mientras se navega por el expediente.
              window.dispatchEvent(new CustomEvent("chat:minimize"));
              navigate(`/review/${conversation.expediente_id}`);
            }}
            className="p-2 rounded hover:bg-orange-50 text-orange-600 hover:text-orange-700"
            data-testid="chat-go-expediente"
            title="Abrir expediente referenciado"
          >
            <FolderOpen className="w-4 h-4" weight="fill" />
          </button>
        )}
        <button
          onClick={() => toggleMute(conversation.id, !(conversation.muted_by || []).includes(user.id))}
          className={`p-2 rounded hover:bg-muted/40 ${
            (conversation.muted_by || []).includes(user.id) ? "text-muted-foreground" : "text-muted-foreground"
          }`}
          data-testid="chat-toggle-mute"
          title={(conversation.muted_by || []).includes(user.id) ? "Activar notificaciones" : "Silenciar"}
        >
          {(conversation.muted_by || []).includes(user.id)
            ? <BellSlash className="w-4 h-4" weight="fill" />
            : <Bell className="w-4 h-4" weight="bold" />}
        </button>
        <button
          onClick={() => setSearching((v) => !v)}
          className="p-2 rounded hover:bg-muted/40 text-muted-foreground"
          data-testid="chat-toggle-search"
          title="Buscar"
        >
          <MagnifyingGlass className="w-4 h-4" weight="bold" />
        </button>
      </div>

      {/* Search row */}
      {searching && (
        <div className="px-4 py-2 border-b border-border flex gap-2">
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Buscar mensajes..."
            data-testid="chat-search-msgs"
            className="h-8 text-sm"
          />
          <Button size="sm" onClick={runSearch} className="h-8">Buscar</Button>
        </div>
      )}
      {searching && searchResults.length > 0 && (
        <div className="px-4 py-2 border-b border-border max-h-40 overflow-y-auto bg-muted/30 text-xs">
          {searchResults.map((m) => (
            <div key={m.id} className="py-1">
              <div className="font-semibold">{sendersById[m.sender_id]?.name || "?"}</div>
              <div className="text-muted-foreground truncate">{m.content}</div>
            </div>
          ))}
        </div>
      )}

      {/* Permiso de notificaciones */}
      {notifPermission === "default" && (
        <div className="px-4 py-2 border-b border-border bg-amber-50 text-amber-900 text-xs flex items-center gap-2">
          <Bell className="w-4 h-4" weight="fill" />
          <span className="flex-1">Activa las notificaciones para no perderte mensajes cuando no tengas la pestaña abierta.</span>
          <Button
            size="sm"
            variant="default"
            onClick={requestNotifications}
            data-testid="chat-enable-notifs"
            className="h-7"
          >
            Activar
          </Button>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="chat-messages-pane">
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground text-xs mt-8">
            No hay mensajes todavía. Sé el primero en escribir.
          </div>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              msg={m}
              mine={m.sender_id === user.id}
              sender={sendersById[m.sender_id]}
              onEdit={editMessage}
              onDelete={deleteMessage}
              onReply={setReplyTo}
              onMentionClick={
                conversation?.expediente_id
                  ? () => navigate(`/review/${conversation.expediente_id}`)
                  : null
              }
            />
          ))
        )}
      </div>

      {/* Reply preview */}
      {replyTo && (
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center gap-2 text-xs">
          <ArrowUDownLeft className="w-3 h-3" />
          <span className="font-semibold">{sendersById[replyTo.sender_id]?.name || "?"}:</span>
          <span className="truncate flex-1 text-muted-foreground">{replyTo.content}</span>
          <button onClick={() => setReplyTo(null)} className="p-1"><XIcon className="w-3 h-3" /></button>
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-border p-3 shrink-0">
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { onPickFiles(e.target.files); e.target.value = ""; }}
          data-testid="chat-file-input"
        />
        <div className="flex gap-2 items-end">
          <button
            onClick={() => fileRef.current?.click()}
            className="p-2 rounded-md text-muted-foreground hover:text-brand hover:bg-muted/40"
            data-testid="chat-attach-btn"
            title="Adjuntar"
          >
            <Paperclip className="w-5 h-5" weight="bold" />
          </button>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Escribe un mensaje... (usa @código para mencionar)"
            rows={1}
            className="text-sm resize-none min-h-[40px] max-h-32 flex-1"
            data-testid="chat-input"
          />
          <Button
            onClick={send}
            disabled={!text.trim() || sending}
            className="h-10 px-3"
            data-testid="chat-send-btn"
          >
            <PaperPlaneRight className="w-4 h-4" weight="fill" />
          </Button>
        </div>
      </div>

      {/* Members modal */}
      <Dialog open={showMembers} onOpenChange={setShowMembers}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Miembros · {(conversation.member_details || []).length}</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {(conversation.member_details || []).map((m) => (
              <div key={m.id} className="flex items-center gap-2.5 py-1.5">
                <Avatar className="h-8 w-8 ring-1 ring-brand/20 bg-brand/10 text-brand">
                  <AvatarFallback className="bg-transparent text-[10px] font-semibold">{m.initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.name}{m.id === user.id && " (tú)"}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {m.code} · {ROLE_LABEL[m.role] || m.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== New Conversation Modal ============================== */

export function NewConversationModal({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("direct");        // direct | group
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setGroupName(""); setFilter(""); setTab("direct");
    api.get("/chat/users").then((r) => setUsers(r.data || []));
  }, [open]);

  const filtered = users.filter((u) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return u.name.toLowerCase().includes(q) || (u.code || "").toLowerCase().includes(q);
  });

  const toggle = (id) => {
    if (tab === "direct") setSelected([id]);
    else setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const create = async () => {
    if (selected.length === 0) return toast.error("Selecciona al menos un usuario");
    if (tab === "group" && !groupName.trim()) return toast.error("Ponle un nombre al grupo");
    setCreating(true);
    try {
      const payload = tab === "direct"
        ? { type: "direct", members: [...selected, user.id] }
        : { type: "group", name: groupName.trim(), members: [...selected, user.id] };
      const { data } = await api.post("/chat/conversations", payload);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva conversación</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 bg-muted p-1 rounded-md">
          {[
            { k: "direct", label: "Directo", icon: UserIcon },
            { k: "group", label: "Grupo", icon: UsersThree },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); setSelected([]); }}
              data-testid={`chat-new-tab-${t.k}`}
              className={`flex-1 text-xs font-semibold py-1.5 rounded flex items-center justify-center gap-1.5 transition-colors ${
                tab === t.k ? "bg-white shadow-sm" : "text-muted-foreground"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" weight={tab === t.k ? "fill" : "bold"} />
              {t.label}
            </button>
          ))}
        </div>
        {tab === "group" && (
          <Input
            placeholder="Nombre del grupo"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            data-testid="chat-new-group-name"
          />
        )}
        <Input
          placeholder="Buscar usuario..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="chat-new-user-search"
        />
        <div className="max-h-72 overflow-y-auto space-y-1 border border-border rounded-md p-1">
          {filtered.map((u) => {
            const checked = selected.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                data-testid={`chat-new-user-${u.id}`}
                className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded transition-colors text-left ${
                  checked ? "bg-brand/10" : "hover:bg-muted/40"
                }`}
              >
                <Avatar className="h-8 w-8 ring-1 ring-brand/20 bg-brand/10 text-brand">
                  <AvatarFallback className="bg-transparent text-[10px] font-semibold">{u.initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-[10px] text-muted-foreground">{u.code} · {ROLE_LABEL[u.role] || u.role}</div>
                </div>
                {checked && <Check className="w-4 h-4 text-brand" weight="bold" />}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-6">Sin resultados</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={create} disabled={creating} data-testid="chat-new-create-btn">
            {creating ? "Creando..." : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
